import React, { FC, Fragment, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@material-ui/core';
import * as Sentry from '@sentry/react';

import { GetFeedQueryVariables, UserFeedFollowGroupFragment } from 'api';
import { Dimensions, Loaders, PaddedPaper } from 'components';
import { DimensionsProvider, QueryVariablesProvider } from 'providers';
import { useGetFeed } from 'hooks';
import { FeedItem } from 'features/content';
import { NULL } from 'app-constants';

interface IProps {
  emptyState: () => JSX.Element;
  header?: ReactNode;
  selectedGroup?: UserFeedFollowGroupFragment;
  variables: GetFeedQueryVariables;
}

const LOADER_HEIGHT = 32;

const FeedManager: FC<IProps> = props => {
  const { emptyState, header, selectedGroup, variables } = props;
  const { t } = useTranslation();

  const { results, sentinelRef, isEmpty, isExhausted, isInitialLoad } = useGetFeed(variables);

  function handleFeedError() {
    return (
      <PaddedPaper>
        <Typography>{t('errors:feed')}</Typography>
      </PaddedPaper>
    );
  }

  function renderLoadMoreLoader() {
    if (isEmpty || isInitialLoad || isExhausted) {
      return <div style={{ height: LOADER_HEIGHT }} />;
    }

    return <Loaders.Standard display="block" height={LOADER_HEIGHT} />;
  }

  function renderBody() {
    if (isInitialLoad) {
      return <Loaders.Feed />;
    }

    if (isEmpty) {
      return emptyState();
    }

    return (
      <Fragment>
        {results.map(result => (
          <Sentry.ErrorBoundary key={result.content_id} fallback={handleFeedError}>
            <FeedItem content={result} selectedGroup={selectedGroup} />
          </Sentry.ErrorBoundary>
        ))}
      </Fragment>
    );
  }

  return (
    <Dimensions>
      {dimensions => (
        <DimensionsProvider value={{ dimensions }}>
          <QueryVariablesProvider value={variables}>
            {header}
            {renderBody()}
            <div ref={sentinelRef}>{renderLoadMoreLoader()}</div>
          </QueryVariablesProvider>
        </DimensionsProvider>
      )}
    </Dimensions>
  );
};

FeedManager.defaultProps = {
  header: NULL,
  selectedGroup: NULL,
};

export default FeedManager;
